import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CalendarCheck, Mail } from 'lucide-react';
import { business } from '@/content/business';
import { usePageMeta } from '@/hooks/usePageMeta';

interface RequestState {
  mode?: 'request';
  name?: string;
  email?: string;
}

/** Schedulers pass the start time under different names; accept the common ones. */
function bookedStart(params: URLSearchParams) {
  const raw = params.get('start') ?? params.get('startTime') ?? params.get('event_start_time');
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

const StartConfirmed = () => {
  const [params] = useSearchParams();
  const state = (useLocation().state ?? {}) as RequestState;
  const booked = params.get('status') === 'booked';
  const isRequest = !booked && state.mode === 'request';
  const start = booked ? bookedStart(params) : null;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const title = booked
    ? 'Your discovery call is booked.'
    : isRequest
      ? 'Your discovery call request is in.'
      : 'Thanks — we’ve got your details.';
  usePageMeta(booked ? 'Discovery call booked' : 'Request received');

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-16 sm:px-6 md:pt-24">
      <span className="mb-8 flex h-14 w-14 items-center justify-center rounded-full bg-goldcore/15 text-goldcore">
        {booked ? (
          <CalendarCheck className="h-7 w-7" aria-hidden="true" />
        ) : (
          <Mail className="h-7 w-7" aria-hidden="true" />
        )}
      </span>
      <h1 className="mb-8 text-[clamp(2.75rem,5vw,4.5rem)] leading-none">{title}</h1>

      {booked && (
        <div className="surface surface-lit mb-10 p-6">
          {start ? (
            <dl className="grid gap-4 sm:grid-cols-3">
              <div>
                <dt className="meta-label mb-1">Date</dt>
                <dd className="font-serif text-2xl">
                  {start.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                </dd>
              </div>
              <div>
                <dt className="meta-label mb-1">Time</dt>
                <dd className="font-serif text-2xl">
                  {start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </dd>
              </div>
              <div>
                <dt className="meta-label mb-1">Timezone</dt>
                <dd className="font-serif text-2xl">{timezone.replace(/_/g, ' ')}</dd>
              </div>
            </dl>
          ) : null}
          <p className={start ? 'mt-5 text-mist' : 'text-mist'}>
            Your confirmation email has the meeting link and calendar invite. Use the link in that email to reschedule
            or cancel.
          </p>
        </div>
      )}

      {isRequest && (
        <div className="surface mb-10 p-6 text-mist">
          <p>
            This is a request, not a confirmed booking. We’ll reply
            {state.email ? (
              <>
                {' '}
                to <span className="text-softwhite">{state.email}</span>
              </>
            ) : null}{' '}
            to confirm a specific time for your {business.discoveryMinutes}-minute call.
          </p>
          <p className="mt-3">
            Need to change your availability? Email{' '}
            <a className="text-link" href={`mailto:${business.email}`}>
              {business.email}
            </a>
            .
          </p>
        </div>
      )}

      <h2 className="mb-3 text-3xl">How to prepare</h2>
      <p className="body-copy mb-4">
        Bring one example of the task you would like to improve. We’ll discuss how it works today, what information is
        available, and what a focused pilot could test.
      </p>
      <p className="mb-10 text-sm text-mist">
        Please do not send passwords or sensitive customer information ahead of the call.
      </p>

      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <Link to="/pilot" className="text-link">
          Read about the pilot
        </Link>
        <Link to="/use-cases" className="text-link">
          Explore use cases
        </Link>
        <Link to="/" className="text-link">
          Back to home
        </Link>
      </div>
    </div>
  );
};

export default StartConfirmed;
